<template>
    <div class="report-container">
        <div class="header">
            <router-link :to="`/management/course/${this.productVariantData.id}/content`" class="back-link">
                <svg width="14" height="14" viewBox="0 0 0.56 0.56" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M.385.52A.02.02 0 0 1 .373.515L.174.317a.053.053 0 0 1 0-.074L.372.045A.017.017 0 1 1 .397.07L.198.268a.017.017 0 0 0 0 .025l.198.198a.017.017 0 0 1 0 .025.02.02 0 0 1-.012.006"
                        fill="var(--primary-color)" />
                </svg>
            </router-link>
            <div>
                <h5>Live Class Flag Report</h5>
            </div>
        </div>

        <div class="content-area">
            <div v-if="initialLoading" class="initial-loading">
                <div class="loading-spinner"></div>
                <div class="loading-text">
                    <div v-if="loadingPhase === 'users'">Loading users...</div>
                    <div v-else-if="loadingPhase === 'content'">Loading content...</div>
                    <div v-else-if="loadingPhase === 'summary'">Loading attendance data...</div>
                    <div v-else>Initializing...</div>
                </div>
            </div>

            <div v-else>
                <div class="search-filter d-flex align-items-center gap-3 flex-wrap">
                    <div class="search">
                        <ItemSelector :initialItems="getSelectedUserObjects()" :items="userOptionsForDropdown"
                            @items-changed="handleUserSelectionChange" :searchKeys="['id', 'firstName', 'lastName']"
                            :multiple="true">
                            <template #trigger-button="{ toggleMenu, selectedItems }">
                                <div class="custom-select-trigger" @click="toggleMenu">
                                    <span v-if="selectedItems.length === 0" class="placeholder-text">Select Users</span>
                                    <span v-else class="selected-count">
                                        {{ selectedItems.length }} User{{ selectedItems.length === 1 ? '' : 's' }} Selected
                                    </span>
                                    <i class="fa fa-chevron-down dropdown-icon"></i>
                                    <i v-if="selectedItems.length > 0" class="fa fa-times clear-icon"
                                        @click.stop="clearSelection" title="Clear selection"></i>
                                </div>
                            </template>

                            <template #item-builder="{ item }">
                                <span>{{ item.displayName }}</span>
                            </template>
                        </ItemSelector>

                        <button @click="handleUserSelection" :disabled="loading"
                            class="filter-user-type-btn search-btn active ms-3">
                            <i class="fa fa-search me-2"></i>
                            {{ loading ? 'Searching...' : 'Search' }}
                        </button>
                        <button @click="exportReport" :disabled="loading || exportLoading || !filteredContents.length"
                            class="filter-user-type-btn search-btn active">
                            <i class="fas fa-file-csv me-2"></i>
                            {{ exportLoading ? "Exporting..." : "Export" }}
                        </button>
                    </div>

                    <date-range-picker ref="dateRangePicker" :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy' }"
                        :showWeekNumbers="true" :showDropdowns="true" :autoApply="true" v-model="dateRangeFilter"
                        class="date-input" control-container-class="d-flex align-items-center gap-3" opens="left"
                        :ranges="dateRangePresets" :maxDate="new Date()">
                        <template v-slot:input="picker" style="min-width: 350px">
                            <i class="fal fa-calendar-alt"></i>
                            <span class="text-sm" v-if="picker.startDate || picker.endDate">
                                {{ formatDate(picker.startDate) }} -
                                {{ formatDate(picker.endDate) }}
                            </span>
                            <span class="text-sm" style="font-size: 12px" v-else>
                                Select Date Range
                            </span>
                            <i @click="resetDateRangeFilter" class="fa fa-times ms-auto cursor-pointer"
                                v-if="picker.startDate || picker.endDate"></i>
                            <i v-else class="fa fa-caret-down ms-auto"></i>
                        </template>
                    </date-range-picker>

                    <div class="btn-group flex-nowrap gap-2">
                        <button @click="setEnrollmentStatus(ProductVariantSubscriptionStatus.ALLOWED)"
                            :disabled="loading"
                            :class="['filter-user-type-btn', { active: activeUserStatuses.includes(ProductVariantSubscriptionStatus.ALLOWED) }]">
                            Enrolled
                        </button>
                        <button @click="setEnrollmentStatus(ProductVariantSubscriptionStatus.UNENROLLED)"
                            :disabled="loading"
                            :class="['filter-user-type-btn', { active: activeUserStatuses.includes(ProductVariantSubscriptionStatus.UNENROLLED) }]">
                            Unenrolled
                        </button>
                        <button @click="setEnrollmentStatus(ProductVariantSubscriptionStatus.BLOCKED)"
                            :disabled="loading"
                            :class="['filter-user-type-btn', { active: activeUserStatuses.includes(ProductVariantSubscriptionStatus.BLOCKED) }]">
                            Blocked
                        </button>
                    </div>
                </div>

                <div class="table-container">
                    <div class="table-wrapper">
                        <div v-if="loading && !isLoadingMore" class="table-loading">
                            <div class="loading-spinner"></div>
                            <div class="loading-text">Loading attendance data...</div>
                        </div>

                        <table v-if="!loading && loadedUsers.length && filteredContents.length">
                            <thead class="sticky">
                                <tr>
                                    <th rowspan="2" class="sticky student">Students</th>
                                    <th colspan="3" class="sticky flags flags-header">Flags</th>
                                    <th rowspan="2" v-for="content in filteredContents" :key="content.id"
                                        class="content-header" :title="content.title">
                                        <div class="content-cell">
                                            <div class="content-title">{{ content.title }}</div>
                                            <div class="content-date"> 
                                                <svg width="10" height="10" viewBox="0 0 0.6 0.6" xmlns="http://www.w3.org/2000/svg"><path d="M.475.1h-.05V.075a.025.025 0 0 0-.05 0V.1h-.15V.075a.025.025 0 0 0-.05 0V.1h-.05A.075.075 0 0 0 .05.175v.3A.075.075 0 0 0 .125.55h.35A.075.075 0 0 0 .55.475v-.3A.075.075 0 0 0 .475.1M.5.475A.024.024 0 0 1 .475.5h-.35A.024.024 0 0 1 .1.475V.3h.4ZM.5.25H.1V.175A.024.024 0 0 1 .125.15h.05v.025a.025.025 0 0 0 .05 0V.15h.151v.025a.025.025 0 0 0 .05 0V.15h.05a.024.024 0 0 1 .025.025Z" /></svg> 
                                                {{ formatDateTime(content.availableFrom) }} </div>
                                            <div class="content-duration"> 
                                                <svg width="10" height="10" viewBox="0 0 0.3 0.3" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" d="M.15.025a.125.125 0 1 1 0 .25.125.125 0 0 1 0-.25m0 .025a.1.1 0 1 0 0 .2.1.1 0 0 0 0-.2m0 .025a.013.013 0 0 1 .012.011l.001.002v.057l.021.021a.013.013 0 0 1-.017.019L.166.184.141.159.137.152.138.15V.088A.013.013 0 0 1 .151.075" /></svg> 
                                                {{ secondsToHMS(getContentTotalDurationInSeconds(content.id)) }}
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                                <tr>
                                    <th class="sticky red">Red</th>
                                    <th class="sticky yellow">Yellow</th>
                                    <th class="sticky green">Green</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="user in loadedUsers" :key="user.id">
                                    <td class="sticky student text-start text-bold cursor"
                                        v-b-tooltip.hover.righttop.html="generateUserInformationTooltip(user)">
                                        <div style="font-size: .85rem">
                                            {{ user.id }} - {{ user.firstName }} {{ user.lastName }}
                                        </div>
                                        <p class="meta">{{ user.email }}</p>
                                        <div>
                                            <span class="meta">{{ user.phone }}</span>
                                            <span class="status-dot" :class="`${user.enrollmentStatus.toLowerCase()}-dot`">
                                            </span>
                                        </div>
                                    </td>
                                    <td class="sticky red flag-count flag-count-red">
                                       {{ countFlags(user.id, 'red') }}
                                    </td>
                                    <td class="sticky yellow flag-count flag-count-yellow">
                                       {{ countFlags(user.id, 'yellow') }}
                                    </td>
                                    <td class="sticky green flag-count flag-count-green">
                                       {{ countFlags(user.id, 'green') }}
                                    </td>

                                    <template v-for="content in filteredContents">
                                        <td class="table-cell attendance-cell" 
                                            :class="getAttendanceInfo({ 
                                                percent: getAttendancePercent(user.id, content.id), 
                                                status: getUserLiveClassStatus(user.id, content.id) 
                                            }).class">

                                            <!-- Present -->
                                            <template v-if="isPresent(user.id, content.id)">
                                                <template v-if="getAttendancePercent(user.id, content.id) != null">
                                                    <span class="attendance-percentage">
                                                        {{ formatPercentDisplay(getAttendancePercent(user.id, content.id)) }}
                                                    </span>
                                                    <span class="attendance-attended">
                                                        {{ secondsToHMS(getAttendedSeconds(user.id, content.id)) }}
                                                    </span>
                                                </template>
                                                <template v-else>
                                                    <span style="font-size: large;">✔</span>
                                                </template>

                                                <span v-if="getClassMode(user.id, content.id) === AttendanceType.ONLINE.toLowerCase()" 
                                                    class="mode-icon online" v-b-tooltip.hover title="Online">
                                                    <i class="fas fa-laptop"></i>
                                                </span>
                                                <span v-else-if="getClassMode(user.id, content.id) === AttendanceType.PHYSICAL.toLowerCase()" 
                                                    class="mode-icon physical" v-b-tooltip.hover title="In Person">
                                                    <i class="fas fa-institution"></i>
                                                </span>
                                            </template>

                                            <!-- Absent -->
                                            <template v-else>
                                                <span style="font-size: large;">✖</span>
                                            </template>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                        </table>

                        <p v-if="!loading && !initialLoading && (!filteredUsers.length || !filteredContents.length)"
                            class="no-records">
                            No Records Found
                        </p>

                        <div class="load-more-section" v-if="(filteredUsers.length > 0 && canLoadMore && filteredContents.length > 0) || isLoadingMore">
                            <div v-if="isLoadingMore" class="load-more-loading">
                                <div class="loading-spinner small"></div>
                                <span>Loading more users...</span>
                            </div>
                            <button v-else-if="canLoadMore && !loading" @click="loadMoreUsers()"
                                class="filter-user-type-btn active">
                                Load More
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import { formatToDDMMMYY_HHmm, toShortDateString } from "../../../helpers/FormatHelpers";
import { mapActions, mapState } from "vuex";
import { ContentType } from "../../../constants/content";
import { ProductVariantSubscriptionStatus } from "../../../constants/productVariant";
import ItemSelector from '../../Shared/ItemSelector.vue';
import { AttendanceStatus, AttendanceType } from "../../../constants/attendance";

export default {
    name: 'LiveClassFlagReport',
    data() {
        return {
            searchQuery: '',
            loading: false,
            initialLoading: true,
            loadingPhase: 'users',
            dateRangeFilter: { startDate: null, endDate: null },
            userStatuses: [ 
                ProductVariantSubscriptionStatus.ALLOWED,
                ProductVariantSubscriptionStatus.UNENROLLED,
                ProductVariantSubscriptionStatus.BLOCKED 
            ],
            activeUserStatuses: [ ProductVariantSubscriptionStatus.ALLOWED ],
            isLoadingMore: false,
            loadedUsers: [],
            numOfUsersToLoad: 10,
            selectedUsers: [],
            ContentType,
            ProductVariantSubscriptionStatus,
            AttendanceStatus,
            AttendanceType,
            liveClassAttendanceMap: new Map(),
            users: [],
            contents: [],
            exportLoading: false
        }
    },
    props: { productVariantData: Object },
    components: { DateRangePicker, ItemSelector },
    computed: {
        ...mapState("user", ["usersByProductVariant"]),
        filteredUsers() {
            return this.users.filter(user => {
                const isAnyUserFilteredInDropdown = this.selectedUsers && this.selectedUsers.length > 0;
                const isUserSelectedInDropdown = !isAnyUserFilteredInDropdown || this.selectedUsers.includes(user.id);

                const hasActiveStatuses = this.activeUserStatuses && this.activeUserStatuses.length > 0;
                const isFoundInActiveEnrollmentStatuses = !hasActiveStatuses || this.activeUserStatuses.includes(user.enrollmentStatus);

                return isUserSelectedInDropdown && isFoundInActiveEnrollmentStatuses;
            });
        },
        userOptionsForDropdown() {
            return this.users.map(user => ({
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phone: user.phone,
                displayName: `${user.id} - ${user.firstName} ${user.lastName}`
            }));
        },
        canLoadMore() {
            return this.filteredUsers.length > this.loadedUsers.length;
        },
        filteredContents() {
            if (!this.dateRangeFilter.startDate || !this.dateRangeFilter.endDate) {
                return this.contents;
            }

            const start = new Date(this.dateRangeFilter.startDate);
            start.setHours(0, 0, 0, 0);
            
            const end = new Date(this.dateRangeFilter.endDate);
            end.setHours(23, 59, 59, 999);

            return this.contents.filter(content => {
                const availableFrom = new Date(content.availableFrom);
                const availableTo = new Date(content.availableTo);
                return availableFrom <= end && availableTo >= start;
            });
        },
        dateRangePresets() {
            let today = new Date();
            today.setHours(0, 0, 0, 0);

            let yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            let thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
            let thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return {
                Today: [today, today],
                Yesterday: [yesterday, yesterday],
                "This month": [thisMonthStart, thisMonthEnd],
                "Last month": [
                    new Date(today.getFullYear(), today.getMonth() - 1, 1),
                    new Date(today.getFullYear(), today.getMonth(), 0),
                ],
                "Last 3 months": [
                    new Date(today.getFullYear(), today.getMonth() - 2, 1),
                    today,
                ],
                "Last 6 months": [
                    new Date(today.getFullYear(), today.getMonth() - 5, 1),
                    today,
                ],
                "This year": [
                    new Date(today.getFullYear(), 0, 1),
                    new Date(today.getFullYear(), 11, 31),
                ],
                "Last year": [
                    new Date(today.getFullYear() - 1, 0, 1),
                    new Date(today.getFullYear() - 1, 11, 31),
                ],
            };
        },
    },
    methods: {
        ...mapActions('user', ['fetchUsersByProductVariant']),
        ...mapActions('content', ['fetchContentListByProductVariant', 'fetchLiveClassAttendanceSummary', 'exportUsersLiveClassFlagReport']),

        setEnrollmentStatus(status) {
            if (!Array.isArray(this.activeUserStatuses)) this.activeUserStatuses = [];
            const index = this.activeUserStatuses.indexOf(status);
            if (index > -1) this.activeUserStatuses.splice(index, 1);
            else this.activeUserStatuses.push(status);

            this.handleUserSelection()
        },

        getContentTotalDurationInSeconds(contentId) {
            const content = this.contents?.find(c => c.id === contentId);
            if (!content || !content.availableFrom || !content.availableTo) return 0;
            const from = new Date(content.availableFrom);
            const to = new Date(content.availableTo);
            const diff = (to - from) / 1000;
            return diff > 0 ? diff : 0;
        },

        getAttendedSeconds(userId, contentId) {
            const record = this.liveClassAttendanceMap?.get(userId)?.get(contentId);
            if (!record) return 0;

            const attended = record.attendedDurationInSeconds || 0;
            const total = record.totalDurationInSeconds || 0;
            return Math.min(attended, total);
        },

        getAttendancePercent(userId, contentId) {
            const record = this.liveClassAttendanceMap?.get(userId)?.get(contentId);
            if (!record) return null;

            const total = record.totalDurationInSeconds || 0;
            const rawAttended = record.attendedDurationInSeconds;

            if (rawAttended == null) return null; 

            if (total === 0) return 0;

            const attended = Math.min(rawAttended, total);
            const percent = (attended / total) * 100;

            return Math.min(100, Number.isInteger(percent) ? percent : Number(percent.toFixed(2)));
        },


        formatPercentDisplay(percent) {
            if (percent === null || percent === undefined) return "0%";
            return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(2)}%`;
        },

        getAttendanceInfo({ percent = null, status = null }) {
            const isPresent = status?.toLowerCase() === AttendanceStatus.PRESENT.toLowerCase();

            if (!isPresent) {
                return { class: "accent-red", flag: "red" };
            }
            if (percent == null) {
                // Present but no duration 
                return { class: "accent-green", flag: "green" };
            }
            if (percent < 50) {
                return { class: "accent-yellow", flag: "yellow" };
            }

            return { class: "accent-green", flag: "green" };
        },

        getUserLiveClassStatus(userId, contentId) {
           const record = this.liveClassAttendanceMap?.get(userId)?.get(contentId);
           if(!record) return AttendanceStatus.ABSENT.toLowerCase();
           return record?.liveClassAttendanceStatus?.toLowerCase();
        },

        isPresent(userId, contentId) {
           return this.getUserLiveClassStatus(userId, contentId) === AttendanceStatus.PRESENT.toLowerCase();
        },

        getClassMode(userId, contentId) {
           const record = this.liveClassAttendanceMap?.get(userId)?.get(contentId);
           return record?.attendedFrom?.toLowerCase();
        },

        countFlags(userId, flagType) {
            const includedContentIds = new Set(this.filteredContents.map(content => content.id));
            const contentMap = this.liveClassAttendanceMap?.get(userId);
            
            if (!contentMap) {
                return flagType === 'red' ? includedContentIds.size : 0;
            }

            let count = 0;
            let seenContentIds = new Set();

            contentMap.forEach(record => {
                const contentId = String(record.contentID);
                if (includedContentIds.has(contentId)) {     
                    seenContentIds.add(contentId);
                    if (record.flag === flagType) count++;
                }
            });
            
            // for records which were not present in the attendance Map
            if (flagType == 'red') {
                count += includedContentIds.size - seenContentIds.size;
            }

            return count;
        },

        secondsToHMS(seconds) {
            if (!seconds || isNaN(seconds)) return "00:00:00";

            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = (seconds % 60).toFixed(0);

            return [h, m, s].map(v => v.toString().padStart(2, "0")).join(":");
        },

        async fetchSubscribedUsersByProductVariant() {
            try {
                this.loadingPhase = 'users';
                await this.fetchUsersByProductVariant(this.productVariantData.id);
                if (this.usersByProductVariant && this.usersByProductVariant[this.productVariantData.id]) {
                    this.users = this.usersByProductVariant[this.productVariantData.id].slice().sort((a, b) => a.id - b.id);
                }
            } catch (error) {
                this.$toasted.error('Failed to fetch users', {
					icon: "exclamation-triangle",
					duration: 3000,
					theme: "outline",
					position: "bottom-center",
				});
            }
        },

        async fetchContentList() {
            try {
                this.loadingPhase = 'content';
                const response = await this.fetchContentListByProductVariant(
                    { id: this.productVariantData.id, contentType: ContentType.LIVE_CLASS.toLowerCase() }
                );
                this.contents = (response || []).sort((a, b) => new Date(a.availableFrom) - new Date(b.availableFrom));

            } catch (error) {
                this.$toasted.error('Failed to fetch content list', {
					icon: "exclamation-triangle",
					duration: 3000,
					theme: "outline",
					position: "bottom-center",
				});
            }
        },

        async resetAndLoadUsers() {
            this.loadedUsers = [];
            await this.loadMoreUsers();
        },

        async loadMoreUsers() {
            if (!this.canLoadMore) return;

            try {
                this.isLoadingMore = true;
                this.loadingPhase = 'summary';
                const nextUsers = this.filteredUsers.slice(this.loadedUsers.length, this.loadedUsers.length + this.numOfUsersToLoad);
                const userIds = nextUsers.map(user => Number(user.id));

                await this.fetchLiveClassData(userIds);

                this.loadedUsers = [...this.loadedUsers, ...nextUsers];

            } catch (err) {
                this.$toasted.error("Failed to load more users.", {
					icon: "exclamation-triangle",
					duration: 3000,
					theme: "outline",
					position: "bottom-center",
				});
            } finally {
                this.isLoadingMore = false;
            }
        },

        clearSelection() {
            this.selectedUsers = [];
            this.handleUserSelection();
        },

        async handleUserSelection() {
            try {
                this.loading = true;

                this.loadedUsers = [];

                const filteredUserIds = this.filteredUsers.map(u => u.id);
                filteredUserIds.forEach(userId => {
                    if (this.liveClassAttendanceMap.has(userId)) {
                        this.liveClassAttendanceMap.delete(userId);
                    }
                });

                await this.loadMoreUsers();
            } catch (err) {
                this.$toasted.error("Failed to filter users.", {
					icon: "exclamation-triangle",
					duration: 3000,
					theme: "outline",
					position: "bottom-center",
				});
            } finally {
                this.loading = false;
            }
        },

        async fetchLiveClassData(userIds) {
            try {
                const contentIds = this.contents.map(c => Number(c.id));
                const dataList = await this.fetchLiveClassAttendanceSummary({ contentIds, userIds });

                if (!this.liveClassAttendanceMap) this.liveClassAttendanceMap = new Map();

                dataList.forEach(record => {
                    const userId = String(record.userID);
                    const contentId = String(record.contentID);

                    if (!this.liveClassAttendanceMap.has(userId)) {
                        this.liveClassAttendanceMap.set(userId, new Map());
                    }

                    const total = Number(record.totalDurationInSeconds || 0);
                    const rawAttended = record.attendedDurationInSeconds;
        
                    let percent = null; 
                    let attended = null;

                    if (total === 0) {
                        percent = 0;
                        attended = 0;
                    } else if (rawAttended != null) {
                        attended = Math.min(rawAttended, total);
                        percent = Math.min(100, Math.round((attended / total) * 100));
                    }

                    const status = record.liveClassAttendanceStatus;

                    const flag = this.getAttendanceInfo({ percent, status }).flag;

                    this.liveClassAttendanceMap.get(userId).set(contentId, {
                        ...record,
                        attendedDurationInSeconds: attended,
                        totalDurationInSeconds: total,
                        liveClassAttendanceStatus: status,
                        flag
                    });
                });
            } catch (error) {
                this.$toasted.error("Failed to fetch live class data", {
					icon: "exclamation-triangle",
					duration: 3000,
					theme: "outline",
					position: "bottom-center",
				});
            }
        },

        generateUserInformationTooltip(userSubscription) {
            const enrolledAt = userSubscription.createdAt;
            const currentDateTime = new Date();
            let expiresAt = userSubscription.expiresAt;
            if (this.productVariantData?.endDate) {
                if (!expiresAt || new Date(this.productVariantData.endDate) < new Date(expiresAt)) {
                    expiresAt = this.productVariantData.endDate;
                }
            }
            let tooltip = `<div style='text-align: left;'>${userSubscription.enrollmentStatus}<br>`;
            if (userSubscription.enrollmentStatus.toLowerCase() === ProductVariantSubscriptionStatus.ALLOWED.toLowerCase()) {
                const daysSince = this.daysBetween(enrolledAt, currentDateTime);
                tooltip += `Access since: ${daysSince} days<br>`;
                if (expiresAt) {
                    const daysLeft = this.daysBetween(currentDateTime, expiresAt);
                    if (daysLeft < 0) {
                        tooltip += `Access already expired`;
                    } else {
                        tooltip += `Access will expire after: ${daysLeft} days`;
                    }
                } else {
                    tooltip += `Access for lifetime`;
                }
            }
            tooltip += `</div>`;
            return tooltip;
        },
        daysBetween(start, end) {
            const s = new Date(start);
            const e = new Date(end);
            return Math.floor((e - s) / (1000 * 60 * 60 * 24));
        },

        formatDateTime(dateStr) {
            if (!dateStr) return 'N/A';
            return formatToDDMMMYY_HHmm(dateStr);
        },

        formatDate(date) {
            return toShortDateString(date);
        },

        getSelectedUserObjects() {
            return this.userOptionsForDropdown.filter(user =>
                this.selectedUsers.includes(user.id)
            );
        },

        handleUserSelectionChange(selectedUserObjects) {
            this.selectedUsers = selectedUserObjects.map(user => user.id);
        },
        resetDateRangeFilter() {
            this.dateRangeFilter = { startDate: null, endDate: null };
        },
        async exportReport() {
            try {
                this.exportLoading = true;

                let start = null;
                let end = null;

                if (this.dateRangeFilter.startDate) {
                    start = new Date(this.dateRangeFilter.startDate);
                    start.setHours(0, 0, 0, 0);
                }

                if (this.dateRangeFilter.endDate) {
                    end = new Date(this.dateRangeFilter.endDate);
                    end.setHours(23, 59, 59, 999);
                }

                const requestData = {
                    productVariantID: parseInt(this.productVariantData.id),
                    userIds: (this.selectedUsers || []).map(userId => parseInt(userId)),
                    enrollmentStatuses: this.activeUserStatuses || [],
                    startDate: start,
                    endDate: end
                };

                const result = await this.exportUsersLiveClassFlagReport(requestData);
                if (result && result.url) {
                    const link = document.createElement('a');
                    link.href = result.url;
                    link.setAttribute('download', result.name ? result.name : 'UsersLiveClassFlagReport.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode.removeChild(link);
                    window.URL.revokeObjectURL(result.url);
                }
            } catch (err) {
                const errorMessage = err?.message || 'Some error occured while exporting';

                this.$toasted.error(
                    errorMessage,
                    {
                        icon: "exclamation-triangle",
                        theme: "outline",
                        duration: 3000,
                        position: "bottom-center",
                        singleton: true,
                    }
                );
            } finally {
                this.exportLoading = false;
            }
        },
    },
    async created() {
        try {
            this.initialLoading = true;

            await this.fetchSubscribedUsersByProductVariant();
            await this.fetchContentList();

            if (this.users.length > 0) {
                await this.loadMoreUsers();
            }

        } catch (error) {
            this.$toasted.error('Failed to load data', {
				icon: "exclamation-triangle",
				duration: 3000,
				theme: "outline",
				position: "bottom-center",
			});
        } finally {
            this.initialLoading = false;
            this.loadingPhase = '';
        }
    }
}
</script>

<style scoped>
.report-container {
    padding: 2rem;
    margin: 0 auto;
}

.header {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;
}

.back-link {
    margin-top: .4rem;
    margin-right: 1.5rem;
}

.header h5 {
    margin-top: .3rem;
    margin-bottom: .5rem;
    color: #333;
}

.search-filter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 20px;
    color: black;
}

.search {
    display: flex;
    flex: 1;
}

.search-input {
    width: 100%;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
    margin-right: 10px;
}

.search-icon {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
}

.date-filter {
    display: flex;
    position: relative;
    flex: 1;
    align-items: baseline;
    justify-content: space-between;
    max-width: 17rem;
}

.date-input {
    padding: 5px 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
}

.filter-user-type-btn:not(.active) {
    border: 1px solid #ddd;
}

.search-btn {
    color: black;
    margin-inline: .4rem;
}

.table-container {
    overflow-x: auto;
    border: 1px solid #ddd;
    border-radius: 5px;
}

.table-wrapper {
    max-height: calc(100vh - 17rem);
    overflow: auto;
}

table {
    width: 100%;
    border-collapse: collapse;
    min-width: 800px;
}

th,
td {
    padding: 8px 12px;
    border: 1px solid #ddd;
    text-align: center;
}

thead th {
    background: #f8f8f8;
    font-weight: 500;
    font-size: 1rem;
}

.sticky {
    position: sticky;
    background: #fff;
    border-right: none;
}

.sticky::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background-color: #ddd;
    z-index: 10;
}

thead.sticky {
    top: 0;
    z-index: 6;
}

.student {
    left: 0;
    min-width: 200px;
    z-index: 5;
}

td.student {
    font-size: 80%;
}

.flags-header {
    left: 200px;
}

.red {
    left: 200px;
    min-width: 80px;
    z-index: 4;
}

.yellow {
    left: 280px;
    min-width: 80px;
    z-index: 3;
}

.green {
    left: 360px;
    min-width: 80px;
    z-index: 2;
}

.content-header {
    min-width: 150px;
}

.flag-count {
    font-size: large;
}

.flag-count-red {
    color: #dc3545;
}

.flag-count-yellow {
    color: #ffc107;
}

.flag-count-green {
    color: #28a745;
}

.text-start {
    text-align: left;
}

.text-bold {
    font-weight: bold;
}

.meta {
    color: #6c757d;
    font-size: .75rem;
    line-height: 11px;
    margin: 1px 0;
}

.status-dot {
    width: 6px;
    height: 6px;
    display: inline-block;
    border-radius: 50%;
    margin-left: .7rem;
}

.unenrolled-dot {
    background-color: red;
}

.allowed-dot {
    background-color: green;
}

.blocked-dot {
    background-color: yellow;
}

.table-cell {
    padding: 6px 8px;
    color: #111;
    font-weight: 600;
    font-size: small;
}

.table-cell .attendance-percentage {
    font-size: medium;
    display: block;
    margin-bottom: .5rem;
}

.attendance-cell {
  position: relative;
}

.attendance-cell .mode-icon {
  position: absolute;
  top: 7px;    
  right: 7px;  
  font-size: 12px; 
  color: #666;
  cursor: pointer;
}

.attendance-cell .mode-icon.physical {
    font-size: 1.1rem;
}

.content-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.content-title {
    font-weight: 600;
    font-size: 12px;
    margin-bottom: 4px;
    text-align: center;
    white-space: normal;
}

.content-date {
    font-size: 9px;
    color: #666;
    margin-bottom: 2px;
}

.content-duration {
    font-size: 9px;
    color: #444;
}

.accent-red {
    background: linear-gradient(180deg, #fdebec, #f8cfd1);
    color: #a94442;
}

.accent-yellow {
    background: linear-gradient(180deg, #fffdf0, #fff5cc);
    color: #8a6d3b;
}

.accent-green {
    background: linear-gradient(180deg, #edfdf2, #c6f0d1);
    color: #3c763d;
}

.no-records {
    text-align: center;
    padding: 40px;
    color: #666;
}

.cursor {
    cursor: pointer;
}

.custom-select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background: white;
    cursor: pointer;
    min-width: 150px;
    font-size: 1rem;
    position: relative;
}

.custom-select-trigger:hover {
    border-color: #CD222D;
}

.placeholder-text {
    color: #6c757d;
}

.selected-count {
    color: #333;
    font-weight: 500;
}

.dropdown-icon {
    color: #6c757d;
    font-size: 12px;
    transition: transform 0.2s ease;
    margin-left: auto;
}

.custom-select-trigger:hover .dropdown-icon {
    color: #CD222D;
}

.clear-icon {
    margin-left: 8px;
    color: #888;
    cursor: pointer;
}

.clear-icon:hover {
    color: #e74c3c;
}

.initial-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    min-height: 300px;
}

.table-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #CD222D;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
}

.loading-spinner.small {
    width: 20px;
    height: 20px;
    border-width: 2px;
    margin-bottom: 0;
    margin-right: 0.5rem;
}

.loading-text {
    font-size: 16px;
    color: #666;
    font-weight: 500;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.load-more-section {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    border-top: 1px solid #eee;
    margin-top: 1rem;
}

.load-more-btn {
    background: #CD222D;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.load-more-btn:hover {
    background: #b01e28;
}

.load-more-loading {
    display: flex;
    align-items: center;
    color: #666;
    font-size: 14px;
}

.all-loaded {
    color: #666;
    font-size: 14px;
    font-style: italic;
}

</style>

<style>.report-container .range-picker-wrapper {
    display: flex;
    align-items: baseline;
}</style>
